import { useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { theme } from '@/styles/theme';
import { Container, Section } from '@/components/ui/Container';
import { Input, TextArea, Label, InputWrapper, ErrorMessage } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Toast } from '@/components/ui/Dialog';
import { FadeInWhenVisible } from '@/components/animations/FadeInWhenVisible';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const PageHeader = styled(Section)`
  background: ${theme.colors.primary.navy};
  color: ${theme.colors.neutral.white};
  text-align: center;
  padding: ${theme.spacing[24]} 0 ${theme.spacing[16]};
`;

const PageTitle = styled.h1`
  color: ${theme.colors.neutral.white};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[12]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const InfoCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[6]};
`;

const IconCircle = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background: ${theme.colors.primary.navy};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary.gold};
`;

const InfoContent = styled.div`
  h3 {
    color: ${theme.colors.primary.navy};
    margin-bottom: ${theme.spacing[2]};
  }

  p {
    color: ${theme.colors.neutral.gray};
    margin: 0;
  }

  a {
    color: ${theme.colors.primary.royalBlue};
    text-decoration: none;

    &:hover {
      color: ${theme.colors.primary.gold};
    }
  }
`;

const contactSchema = z.object({
  name: z.string().min(2, 'Naam moet minimaal 2 karakters bevatten'),
  email: z.string().email('Ongeldig e-mailadres'),
  subject: z.string().min(3, 'Onderwerp moet minimaal 3 karakters bevatten'),
  message: z.string().min(10, 'Bericht moet minimaal 10 karakters bevatten'),
});

type ContactFormData = z.infer<typeof contactSchema>;

export const ContactPage = () => {
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    console.log('Form data:', data);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    reset();
    setToast({ message: 'Bericht verzonden! We nemen zo snel mogelijk contact met u op.', variant: 'success' });
  };

  return (
    <>
      <PageHeader>
        <Container>
          <PageTitle>Contact</PageTitle>
        </Container>
      </PageHeader>

      <Section>
        <Container>
          <ContactGrid>
            <FadeInWhenVisible direction="left">
              <div>
                <h2 style={{ marginBottom: theme.spacing[6] }}>Neem Contact Op</h2>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <InputWrapper>
                    <Label htmlFor="name">Naam *</Label>
                    <Input
                      id="name"
                      {...register('name')}
                      placeholder="Uw naam"
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
                  </InputWrapper>

                  <InputWrapper>
                    <Label htmlFor="email">E-mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      placeholder="uw.email@voorbeeld.be"
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
                  </InputWrapper>

                  <InputWrapper>
                    <Label htmlFor="subject">Onderwerp *</Label>
                    <Input
                      id="subject"
                      {...register('subject')}
                      placeholder="Onderwerp van uw bericht"
                      aria-invalid={!!errors.subject}
                    />
                    {errors.subject && <ErrorMessage>{errors.subject.message}</ErrorMessage>}
                  </InputWrapper>

                  <InputWrapper>
                    <Label htmlFor="message">Bericht *</Label>
                    <TextArea
                      id="message"
                      {...register('message')}
                      placeholder="Uw bericht..."
                      aria-invalid={!!errors.message}
                    />
                    {errors.message && <ErrorMessage>{errors.message.message}</ErrorMessage>}
                  </InputWrapper>

                  <Button type="submit" size="lg" disabled={isSubmitting}>
                    <Send size={20} />
                    {isSubmitting ? 'Verzenden...' : 'Verstuur Bericht'}
                  </Button>
                </Form>
              </div>
            </FadeInWhenVisible>

            <FadeInWhenVisible direction="right">
              <ContactInfo>
                <h2>Contactgegevens</h2>

                <InfoCard elevated>
                  <IconCircle>
                    <MapPin size={24} />
                  </IconCircle>
                  <InfoContent>
                    <h3>Adres</h3>
                    <p>
                      Gasthuisstraat 1<br />
                      8970 Poperinge<br />
                      België
                    </p>
                  </InfoContent>
                </InfoCard>

                <InfoCard elevated>
                  <IconCircle>
                    <Mail size={24} />
                  </IconCircle>
                  <InfoContent>
                    <h3>E-mail</h3>
                    <p>
                      <a href="mailto:info@kph.be">info@kph.be</a>
                    </p>
                  </InfoContent>
                </InfoCard>

                <InfoCard elevated>
                  <IconCircle>
                    <Phone size={24} />
                  </IconCircle>
                  <InfoContent>
                    <h3>Telefoon</h3>
                    <p>
                      <a href="tel:+32123456789">+32 123 45 67 89</a>
                    </p>
                  </InfoContent>
                </InfoCard>
              </ContactInfo>
            </FadeInWhenVisible>
          </ContactGrid>
        </Container>
      </Section>
      {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
    </>
  );
};
